"use client";
import { BotIcon, ChevronDownIcon } from "lucide-react";
import { forwardRef, useState, useContext } from "react";
import { AssistantModalPrimitive } from "@assistant-ui/react";
import { Thread } from "./thread";
import { TooltipIconButton } from "./tooltip-icon-button";
import { ArrowUpLeftFromSquare, ArrowDownRightFromSquare } from "lucide-react";
import {
  ChatbotWebSocketContext,
  type ChatbotWebSocketContextValue
} from "../../chatbotWssProvider";

export const AssistantModal = () => {
  const [fullHeight, setFullHeight] = useState(false)
  const chatbot = useContext(ChatbotWebSocketContext)

  return (
    <AssistantModalPrimitive.Root onOpenChange={(e) => { if (e == true && !chatbot.connected) { chatbot.connect() } }}>
      <AssistantModalPrimitive.Anchor className={`${fullHeight ? 'bottom-0' : 'fixed bottom-16 right-10 size-14'}`}>
        <AssistantModalPrimitive.Trigger asChild>
          <AssistantModalButton height={fullHeight} />
        </AssistantModalPrimitive.Trigger>
      </AssistantModalPrimitive.Anchor>
      <AssistantModalPrimitive.Content
        id='assistant'
        data-height={fullHeight}
        sideOffset={16}
        className={
          `bg-popover
          origin-bottom-right
        text-primary
        z-50 
        overflow-clip 
        p-0 
        shadow-md 
        outline-none 
        [&>.aui-thread-root]:bg-inherit 
        data-[state=closed]:animate-out 
        data-[state=open]:animate-in 
        data-[state=closed]:fade-out-0 
        data-[state=open]:fade-in-0 
        data-[state=closed]:zoom-out 
        data-[state=open]:zoom-in 
        data-[state=open]:slide-in-from-bottom-1/2 
        data-[state=open]:slide-in-from-right-1/2 
        data-[state=closed]:slide-out-to-bottom-1/2 
        data-[state=closed]:slide-out-to-right-1/2
        data-[height=true]:slide-in-from-bottom-1/2 
        data-[height=true]:slide-in-from-right-1/2 
        data-[height=false]:slide-out-to-bottom-1/2 
        data-[height=false]:slide-out-to-right-1/2
        ${fullHeight ?
            'w-[75vw] h-[calc(100vh-180px)] rounded-l-lg'
            :
            'h-[500px] w-[400px] rounded-xl'}`
        }
      >
        <div className="h-[36px] py-[6px] pl-[6px]" onClick={() => {
          setFullHeight(!fullHeight)
        }}>
          {fullHeight ?
            <ArrowDownRightFromSquare color={'var(--muted-foreground)'} className="cursor-pointer" /> : <ArrowUpLeftFromSquare color={'var(--muted-foreground)'} className="cursor-pointer" />
          }
        </div>
        <div className="h-[calc(100%-36px)]">
          <Thread />
        </div>
      </AssistantModalPrimitive.Content>
    </AssistantModalPrimitive.Root>
  );
};

type AssistantModalButtonProps = { "data-state"?: "open" | "closed", "height"?: true | false };

const AssistantModalButton = forwardRef<
  HTMLButtonElement,
  AssistantModalButtonProps
>(({ "data-state": state, height, ...rest }, ref) => {
  const tooltip = state === "open" ? "Close Assistant" : "Open Assistant";

  return (
    <TooltipIconButton
      variant="default"
      tooltip={tooltip}
      side="left"
      {...rest}
      className={`size-full rounded-full shadow shadow-md transition-transform ${!height ? 'hover:scale-110 active:scale-90' : 'py-4'} bg-background shadow-muted-foreground`}
      ref={ref}
    >
      <BotIcon data-state={state} color={'var(--muted-foreground)'} className="absolute size-8 transition-all data-[state=closed]:rotate-0 data-[state=open]:rotate-90 data-[state=closed]:scale-100 data-[state=open]:scale-0" />
      <ChevronDownIcon
        data-state={state}
        color={'var(--muted-foreground)'}
        className="absolute size-6 transition-all data-[state=closed]:-rotate-90 data-[state=open]:rotate-0 data-[state=closed]:scale-0 data-[state=open]:scale-100"
      />
    </TooltipIconButton>
  );
});

AssistantModalButton.displayName = "AssistantModalButton";
